import React, { useMemo, useState } from 'react';
import { View, Pressable, StyleSheet, Platform, ScrollView } from 'react-native';
import { useTheme } from '@/context/ThemeContext';
import { useTranslation } from '@/hooks/useTranslation';
import useResponsiveness from '@/hooks/useResponsiveness';
import Text from '@/components/Typography/Text';
import Icon from '@/components/Icon';

const isWeb = Platform.OS === 'web';

const TAB_DEFS = [
  { id: null, labelKey: 'explore.allMedia', icon: 'grid-2' },
  { id: 'image', labelKey: 'explore.images', icon: 'image' },
  { id: 'video', labelKey: 'explore.videos', icon: 'video' },
  { id: 'music', labelKey: 'explore.music', icon: 'music' },
  { id: '3d', labelKey: 'explore.models3d', icon: 'cube' },
];

const TabItem = React.memo(({ def, label, active, onPress, colors, compact }) => {
  const [hovered, setHovered] = useState(false);
  const bg = active
    ? colors.solid_color_primary
    : hovered ? colors.solid_color_secondary_dark : 'transparent';
  return (
    <Pressable
      onPress={onPress}
      onHoverIn={() => setHovered(true)}
      onHoverOut={() => setHovered(false)}
      style={[styles.tab, compact && styles.tabCompact, { backgroundColor: bg }]}
    >
      <Icon
        name={def.icon}
        size={compact ? 11 : 12}
        color={active ? colors.text_color_primary : colors.text_color_secondary}
      />
      <Text
        size={compact ? 'xs' : 's'}
        weight="medium"
        color={active ? 'primary' : 'secondary'}
        numberOfLines={1}
      >
        {label}
      </Text>
    </Pressable>
  );
});
TabItem.displayName = 'ExploreMediaTypeTabItem';

const ExploreMediaTypeTabs = React.memo(({ activeType, onChangeType }) => {
  const { colors } = useTheme();
  const { t } = useTranslation();
  const { isXS } = useResponsiveness();

  const items = useMemo(() => TAB_DEFS.map(def => ({
    def,
    label: t(def.labelKey),
    active: activeType === def.id,
  })), [activeType, t]);

  const pillRow = (
    <View style={[styles.container, { backgroundColor: colors.solid_color_tertiary }]}>
      {items.map(({ def, label, active }) => (
        <TabItem
          key={def.id ?? '__all__'}
          def={def}
          label={label}
          active={active}
          onPress={() => onChangeType(def.id)}
          colors={colors}
          compact={isXS}
        />
      ))}
    </View>
  );

  if (isXS) {
    return (
      <ScrollView
        horizontal
        showsHorizontalScrollIndicator={false}
        style={styles.scroll}
        contentContainerStyle={styles.scrollContent}
        keyboardShouldPersistTaps="handled"
      >
        {pillRow}
      </ScrollView>
    );
  }

  return pillRow;
});

ExploreMediaTypeTabs.displayName = 'ExploreMediaTypeTabs';

const styles = StyleSheet.create({
  scroll: {
    width: '100%',
    maxWidth: '100%',
    flexGrow: 0,
  },
  scrollContent: {
    flexGrow: 0,
  },
  container: {
    flexDirection: 'row',
    alignItems: 'center',
    alignSelf: 'flex-start',
    height: 31,
    padding: 3,
    borderRadius: 8,
    gap: 2,
  },
  tab: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    paddingHorizontal: 10,
    height: 25,
    borderRadius: 6,
    flexShrink: 0,
    ...(isWeb ? { cursor: 'pointer', transition: 'background-color 0.15s' } : {}),
  },
  tabCompact: {
    paddingHorizontal: 8,
    gap: 5,
    height: 24,
  },
});

export default ExploreMediaTypeTabs;
